import { Button, Group, Menu, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import {
  PiArrowRight,
  PiArrowsClockwise,
  PiCaretDown,
  PiCheck,
  PiDownloadSimple,
  PiMoney,
  PiPaperPlaneTilt,
  PiPencilSimple,
  PiX,
} from 'react-icons/pi';
import { Link } from 'wouter';
import { cancelOutcome } from '@/components/InvoiceDetailsModal/changeActions';
import {
  ActionButton,
  ReasonItem,
} from '@/components/InvoiceDetailsModal/DocumentCard';
import {
  type DocumentAction,
  proformaBar,
} from '@/components/InvoiceDetailsModal/documentActions';
import {
  queuedSendText,
  queuedSendWarning,
} from '@/components/InvoiceDetailsModal/invoiceActions';
import { usePayerRefresh } from '@/components/InvoiceDetailsModal/RefreshPayerDetails';
import type { InvoiceActionHandlers } from '@/components/InvoicesPage/useInvoiceActions';
import { ApiError } from '@/lib/api';
import {
  type InvoiceDetails,
  type TDocumentGroup,
  useSendInvoice,
} from '@/models/invoices';
import { usePermissions } from '@/models/permissions';

const CANCEL_HINT = {
  'awaits-new-proforma':
    'Leaves the offer with no active proforma; its owner is told.',
  'offer-stays': 'The offer and its other proformas stay as they are.',
  'returns-first-installment':
    'Installment 1 goes back to the schedule; its owner is told.',
  'returns-to-schedule': 'The installment goes back to the schedule.',
  'cancels-renewal': 'Also cancels the renewal it bills.',
  'offer-untouched': 'No offer is touched.',
} as const;

const offerInvoices = (group: TDocumentGroup | undefined) =>
  group
    ? [
        ...group.proformas,
        ...(group.plan?.items.flatMap((i) => i.proformas) ?? []),
      ]
    : [];

/** "Last sent 26.10.2026 21:00 to x@y (v1)", or null when no version was ever sent. */
export function lastSentText(invoice: InvoiceDetails): string | null {
  const last = (invoice.documentVersions?.versions ?? [])
    .flatMap((v) =>
      [...v.sentTo, ...(v.sentBeforeTracking ?? [])].map((d) => ({
        ...d,
        version: v.version,
      })),
    )
    .sort((a, b) => b.sentAt.localeCompare(a.sentAt))[0];
  return last
    ? `Last sent ${dayjs(last.sentAt).format('DD.MM.YYYY HH:mm')} to ${last.email} (v${last.version})`
    : null;
}

function Hint({ children }: { children: string }) {
  return (
    <Text fz={11} c="dimmed" lh={1.3}>
      {children}
    </Text>
  );
}

/**
 * The action bar of a proforma (spec 9.5), above the document: fixed slots Record payment (its
 * menu: Add client payment report), Settle, Send, PDF and the Change menu, then one line per
 * notice. It acts on the current document, never on a version selected in the document area.
 * What concerns the whole offer (Monday, a new proforma, an installment issued again) is on the
 * offer line, and KYC on the payer in Parties.
 */
export function ProformaActionBar({
  invoice,
  handlers,
  group,
}: {
  invoice: InvoiceDetails;
  handlers: InvoiceActionHandlers;
  group?: TDocumentGroup;
}) {
  const { permissions } = usePermissions();
  const send = useSendInvoice();
  const bar = proformaBar(invoice, permissions);
  const refreshGate = bar.change.find((a) => a.key === 'refresh');
  const refresh = usePayerRefresh(
    invoice,
    refreshGate ? refreshGate.reason : 'Not offered.',
  );
  const settled = invoice.status === 'paid';

  const doSend = () =>
    send.mutate(
      { id: Number(invoice.id) },
      {
        onSuccess: (r) =>
          notifications.show({
            color: r.resent ? 'teal' : 'orange',
            title: r.resent ? `Sent to ${r.resentTo}` : 'Queued',
            message:
              r.note ??
              r.error ??
              `Version ${invoice.documentVersion ?? 1} was sent.`,
          }),
        onError: (e) =>
          notifications.show({
            color: 'red',
            title: 'Not sent',
            message: e instanceof ApiError ? e.body : String(e),
          }),
      },
    );

  const later =
    invoice.installments != null &&
    invoice.installments.seq < invoice.installments.total;
  const hint: Partial<Record<DocumentAction['key'], string>> = {
    amend: later
      ? 'Also updates later installments with nothing collected.'
      : 'Records a new version; nothing is sent.',
    refresh: later
      ? 'Also updates later installments of the same payer.'
      : 'Records a new version; nothing is sent.',
    cancel:
      CANCEL_HINT[
        cancelOutcome(
          {
            id: invoice.id,
            offerId: invoice.offerId ?? null,
            renewalId: invoice.renewalId ?? null,
            partNumber: invoice.partNumber,
          },
          offerInvoices(group),
        )
      ],
  };
  const changeClick = {
    amend: () => handlers.onAmend(invoice),
    refresh: refresh.open,
    cancel: () => handlers.onCancelInvoice(invoice),
  } as Record<string, () => void>;
  const changeItems = bar.change.filter(
    (a) => !(a.key === 'refresh' && refresh.hidden),
  );

  const queued =
    invoice.status === 'cancelled' ? [] : (invoice.queuedSends ?? []);

  return (
    <Stack gap={4}>
      <Group justify="space-between" gap={6} wrap="wrap">
        <Group gap={6} wrap="wrap">
          {bar.payment && (
            <Button.Group>
              <Button
                size="xs"
                variant={bar.primary === 'recordPayment' ? 'filled' : 'default'}
                leftSection={<PiPencilSimple size={12} />}
                onClick={() => handlers.onRecordMovement(invoice)}
              >
                Record payment
              </Button>
              <Menu position="bottom-start" withinPortal width={280}>
                <Menu.Target>
                  <Button
                    size="xs"
                    px={6}
                    variant={
                      bar.primary === 'recordPayment' ? 'filled' : 'default'
                    }
                    aria-label="More payment actions"
                  >
                    <PiCaretDown size={12} />
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <ReasonItem
                    label={bar.payment.report.label}
                    reason={bar.payment.report.reason}
                    icon={<PiMoney size={14} />}
                    onClick={() => handlers.onAddPaymentReport(invoice)}
                  />
                </Menu.Dropdown>
              </Menu>
            </Button.Group>
          )}
          {bar.settle && (
            <ActionButton
              label={bar.settle.label}
              reason={bar.settle.reason}
              icon={<PiCheck size={12} />}
              color="teal"
              variant={bar.primary === 'settle' ? 'filled' : 'light'}
              onClick={() => handlers.onSettle(invoice)}
            />
          )}
          {settled && invoice.finalInvoice && (
            <Button
              size="xs"
              color="teal"
              leftSection={<PiArrowRight size={12} />}
              component={Link}
              to={`~/invoices/final/${invoice.finalInvoice.id}`}
            >
              Invoice {invoice.finalInvoice.number}
            </Button>
          )}
          {bar.send && (
            <ActionButton
              label={
                settled && bar.send.label === 'Send to client'
                  ? 'Send proforma copy'
                  : bar.send.label
              }
              reason={bar.send.reason}
              hint={lastSentText(invoice)}
              icon={<PiPaperPlaneTilt size={12} />}
              loading={send.isPending}
              onClick={doSend}
            />
          )}
          <ActionButton
            label="PDF"
            reason={null}
            icon={<PiDownloadSimple size={12} />}
            onClick={() => handlers.onDownloadPDF(invoice)}
          />
        </Group>
        {changeItems.length > 0 && (
          <Menu position="bottom-end" withinPortal width={300}>
            <Menu.Target>
              <Button
                size="xs"
                variant="default"
                rightSection={<PiCaretDown size={12} />}
              >
                Change
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {bar.changeReason ? (
                <Stack gap={2} px="sm" py={6}>
                  <Text fz="sm" c="dimmed">
                    {changeItems.map((a) => a.label).join(', ')}
                  </Text>
                  <Hint>{bar.changeReason}</Hint>
                </Stack>
              ) : (
                changeItems.map((a) => (
                  <ChangeItem
                    key={a.key}
                    action={a}
                    hint={hint[a.key]}
                    onClick={changeClick[a.key]}
                  />
                ))
              )}
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>
      {settled && invoice.settledBeforeFinalInvoices && (
        <Text fz="xs" c="dimmed">
          Settled before final invoices existed.
        </Text>
      )}
      {queued.map((q) => {
        const warning = queuedSendWarning(q);
        return (
          <Text key={q.id} fz="xs" c={warning ? 'orange' : 'blue'}>
            {queuedSendText(q)}
            {warning ? `. ${warning}` : '.'}
          </Text>
        );
      })}
      {refresh.modal}
    </Stack>
  );
}

function ChangeItem({
  action,
  hint,
  onClick,
}: {
  action: DocumentAction;
  hint?: string;
  onClick: () => void;
}) {
  const cancel = action.key === 'cancel';
  return (
    <>
      {cancel && <Menu.Divider />}
      <ReasonItem
        label={cancel ? 'Cancel proforma' : action.label}
        reason={action.reason}
        hint={hint}
        color={cancel ? 'red' : undefined}
        icon={
          cancel ? (
            <PiX size={14} />
          ) : action.key === 'refresh' ? (
            <PiArrowsClockwise size={14} />
          ) : (
            <PiPencilSimple size={14} />
          )
        }
        onClick={onClick}
      />
    </>
  );
}
